import type { CustomObjectListItem } from '@kizen/api/custom-object';
import type { ReactNode } from 'react';
import type { MergeField } from '__components/WYSIWYG/use-merge-fields';
import type { Store } from 'store/types';

import styled from '@emotion/styled';
import { useQuery, useQueries } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Select from '__components/Inputs/Select';
import BasicModal from '__components/Modals/presets/BasicModal';
import {
  useBusinessMergeFields,
  useTeamMemberMergeFields,
} from '__components/WYSIWYG/use-merge-fields';
import {
  customObjectCategoriesQuery,
  customObjectFieldsQuery,
  customObjectsQuery,
} from 'query/custom-object';
import { FORCE_ALL_RECORDS_SIZE } from '__services/helpers';
import { ButtonRadio } from 'ts-components/ButtonToggle';
import { isMergeFieldUnescaped } from 'utility/merge-fields';

type Option<T = string> = { value: T; label: string };

type MergeFieldType =
  | 'automation_variable'
  | 'custom_object'
  | 'business'
  | 'team_member';

type MergeFieldNodeAttributes = {
  objectname: string;
  relationship: string;
  label: string;
  unescaped: boolean;
};

type NonObjectMergeFieldOption = {
  label: string;
  relationship: string;
  value: string;
};

type MergeFieldOption = MergeField & Option;

type MergeFieldModalProps = {
  show: boolean;
  onConfirm: (
    data:
      | MergeFieldNodeAttributes
      | Pick<NonObjectMergeFieldOption, 'label' | 'relationship'>
  ) => void;
  onHide: () => void;
};

type State = {
  object: Option<
    Pick<CustomObjectListItem, 'id' | 'name' | 'object_name'>
  > | null;
  field:
    | (Option & Pick<MergeFieldNodeAttributes, 'relationship' | 'unescaped'>)
    | null;
  automationVariable: NonObjectMergeFieldOption | null;
  business: NonObjectMergeFieldOption | null;
  teamMember: NonObjectMergeFieldOption | null;
};

type MergeFieldModalProviderProps = {
  children: ReactNode;
  /**
   * Only select amongst the object ids specified here.
   */
  allowedCustomObjects?: string[];
  automationVariables?: Array<{ label: string; value: string }>;
  /**
   * Default `false`
   */
  enableAutomationVariableMergeFields?: boolean;
  /**
   * Default `true`
   */
  enableBusinessMergeFields?: boolean;
  /**
   * Default `true`
   */
  enableCustomObjectMergeFields?: boolean;
  /**
   * Default `true`
   */
  enableTeamMemberMergeFields?: boolean;
  /**
   * Will create a link_url "field" as the first item in the fields dropdown for every custom object
   */
  enableRecordLinkFieldOption?: boolean;
};

type MergeFieldModalContext = {
  allowedCustomObjects?: string[];
  automationVariables: MergeFieldModalProviderProps['automationVariables'];
  automationVariableMergeFields?: NonObjectMergeFieldOption[];
  enableRecordLinkFieldOption: boolean;
  enabled: {
    automationVariable: boolean;
    business: boolean;
    customObject: boolean;
    teamMember: boolean;
  };
};

const ctx = createContext<MergeFieldModalContext>({
  allowedCustomObjects: undefined,
  automationVariables: [],
  automationVariableMergeFields: [],
  enableRecordLinkFieldOption: false,
  enabled: {
    automationVariable: false,
    business: true,
    customObject: true,
    teamMember: true,
  },
});

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const StyledSelect = styled(Select)`
  flex: 1;
`;

export const MergeFieldModalProvider = (
  props: MergeFieldModalProviderProps
) => {
  const {
    allowedCustomObjects,
    automationVariables = [],
    enableAutomationVariableMergeFields = false,
    enableBusinessMergeFields = true,
    enableCustomObjectMergeFields = true,
    enableTeamMemberMergeFields = true,
    enableRecordLinkFieldOption = false,
    children,
  } = props;

  const value: MergeFieldModalContext = {
    automationVariables,
    automationVariableMergeFields: [],
    allowedCustomObjects,
    enableRecordLinkFieldOption,
    enabled: {
      automationVariable: enableAutomationVariableMergeFields,
      business: enableBusinessMergeFields,
      customObject: enableCustomObjectMergeFields,
      teamMember: enableTeamMemberMergeFields,
    },
  };

  if (automationVariables) {
    value.automationVariableMergeFields = automationVariables.map((av) => {
      const relationship = `automation_variable.${av.value}`;

      return {
        relationship,
        label: av.label,
        value: relationship,
        key: av.value,
      };
    });
  }

  return <ctx.Provider value={value}>{children}</ctx.Provider>;
};

export const useMergeFieldModalContext = () => {
  return useContext(ctx);
}

export const MergeFieldModal = (props: MergeFieldModalProps) => {
  const { show, onConfirm, onHide } = props;

  const { t } = useTranslation();
  const mfContext = useContext(ctx);

  const {
    allowedCustomObjects,
    automationVariableMergeFields,
    enableRecordLinkFieldOption,
    enabled,
  } = mfContext;

  const [state, setState] = useState<State>({
    object: null,
    field: null,
    automationVariable: null,
    business: null,
    teamMember: null,
  });
  const [type, setType] = useState<MergeFieldType>(() => {
    if (enabled.customObject) {
      return 'custom_object';
    }
    if (enabled.business) {
      return 'business';
    }
    if (enabled.teamMember) {
      return 'team_member';
    }
    if (enabled.automationVariable) {
      return 'automation_variable';
    }
    return 'custom_object';
  });

  const clientObject = useSelector((store: Store) => {
    return store.authentication.chosenBusiness.client_object;
  });

  const showFields = !!state.object;
  const disabled =
    (type === 'custom_object' && !state.field) ||
    (type === 'business' && !state.business) ||
    (type === 'team_member' && !state.teamMember);
  const typeCount = Object.values(enabled).filter(Boolean).length;
  const showTypeSelector = typeCount > 1;
  const selectedObjectName = state.object?.value?.name;
  const showClientObject =
    clientObject &&
    (!allowedCustomObjects || allowedCustomObjects.includes(clientObject.id));

  const businessMergeFields = useBusinessMergeFields({
    forSelectOptions: true,
  });
  const teamMemberMergeFields = useTeamMemberMergeFields({
    forSelectOptions: true,
  });

  const { data: customObjectOptions } = useQuery(
    customObjectsQuery({
      params: { page_size: FORCE_ALL_RECORDS_SIZE },
      select: (data) => {
        let allowed = data.results;

        if (Array.isArray(allowedCustomObjects)) {
          allowed = data.results.filter((obj) => {
            return allowedCustomObjects.includes(obj.id);
          });
        }

        const options = allowed.map((obj) => {
          const value = {
            id: obj.id,
            name: obj.name,
            object_name: obj.object_name,
          };

          return { value, label: obj.object_name };
        });

        if (showClientObject) {
          options.push({
            label: clientObject.object_name,
            value: {
              id: clientObject.id,
              name: 'contact', // note: a contact merge field (unfortunately) does not use the object name 'client_client'
              object_name: clientObject.object_name,
            },
          });
        }

        options.sort((a, b) => {
          return a.value.object_name.localeCompare(b.value.object_name);
        });

        return options;
      },
    })
  );

  const fieldOptions = useQueries({
    queries: [
      customObjectFieldsQuery(state.object?.value.id ?? '', {
        enabled: showFields,
      }),
      customObjectCategoriesQuery(state.object?.value.id ?? '', {
        enabled: showFields,
      }),
    ],
    combine: ([q1, q2]) => {
      const fields = q1.data;
      const categories = q2.data;

      if (!fields || !categories) {
        return undefined;
      }

      const name = state.object!.value.name;

      const categorized = fields.reduce<Record<string, MergeFieldOption[]>>(
        (acc, field) => {
          acc[field.category] = acc[field.category] || [];
          const relationship = `${name}.${field.name}`;
          const option = {
            relationship,
            unescaped: isMergeFieldUnescaped(field),
            value: relationship,
            label: field.display_name,
          };
          acc[field.category].push(option);
          return acc;
        },
        {}
      );

      const options = categories.reduce<
        Array<{ label: string; options: MergeFieldOption[] }>
      >((acc, category, index) => {
        if (categorized[category.id]) {
          if (enableRecordLinkFieldOption && index === 0) {
            const relationship = `${selectedObjectName}.link_url`;
            const link: MergeFieldOption = {
              label: t('Link to Record'),
              relationship,
              value: relationship,
            };
            const options = categorized[category.id] ?? [];
            acc.push({ label: category.name, options: [link, ...options] });
          } else {
            acc.push({
              label: category.name,
              options: categorized[category.id] ?? [],
            });
          }
        }

        return acc;
      }, []);

      return options;
    },
  });

  const handleUpdateObject = useCallback((option: State['object']) => {
    setState({
      object: option,
      field: null,
      automationVariable: null,
      business: null,
      teamMember: null,
    });
  }, []);

  const handleUpdateField = useCallback((option: State['field']) => {
    setState((previous) => {
      return {
        ...previous,
        field: option,
      };
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (type === 'automation_variable' && state.automationVariable) {
      onConfirm({
        relationship: state.automationVariable.relationship,
        label: state.automationVariable.label,
      });
    } else if (type === 'business' && state.business) {
      onConfirm({
        relationship: state.business.relationship,
        label: state.business.label,
      });
    } else if (type === 'team_member' && state.teamMember) {
      onConfirm({
        relationship: state.teamMember.relationship,
        label: state.teamMember.label,
      });
    } else if (type === 'custom_object' && state.object && state.field) {
      onConfirm({
        objectname: state.object.value.object_name,
        relationship: state.field.value,
        label: state.field.label,
        unescaped: state.field.unescaped,
      });
    }
  }, [state, type, onConfirm]);

  const typeOptions: Option<MergeFieldType>[] = [];
  if (enabled.customObject) {
    typeOptions.push({ value: 'custom_object', label: t('Custom Object') });
  }
  if (enabled.business) {
    typeOptions.push({ value: 'business', label: t('Business') });
  }
  if (enabled.teamMember) {
    typeOptions.push({ value: 'team_member', label: t('Team Member') });
  }
  if (enabled.automationVariable) {
    typeOptions.push({
      value: 'automation_variable',
      label: t('Workflow Variable'),
    });
  }

  if (
    type === 'custom_object' &&
    customObjectOptions?.length === 1 &&
    !state.object
  ) {
    setState((previous) => {
      return { ...previous, object: customObjectOptions[0] };
    });
  }

  return (
    <BasicModal
      show={show}
      width={typeCount > 3 ? '600px' : '450px'}
      enforceFocus={false}
      onConfirm={handleConfirm}
      onHide={onHide}
      heading={t('Merge Field')}
      buttonText={t('Insert')}
      defaultLeftBtnText={null}
      disabled={disabled}
      data-qa="multi-source-merge-modal"
    >
      <FormContainer>
        {showTypeSelector && (
          <ButtonRadio
            variant="outline"
            value={type}
            options={typeOptions}
            onChange={(value: MergeFieldType) => {
              setType(value);
              setState({
                object: null,
                field: null,
                automationVariable: null,
                business: null,
                teamMember: null,
              });
            }}
          />
        )}
        {type === 'custom_object' && (
          <>
            <StyledSelect
              value={state.object}
              label={t('Choose Object')}
              placeholder={t('Choose Object')}
              options={customObjectOptions}
              onChange={handleUpdateObject}
              inModal
              menuInline
            />
            {showFields ? (
              <StyledSelect
                value={state.field}
                label={t('Choose Field')}
                placeholder={t('Choose Field')}
                options={fieldOptions}
                onChange={handleUpdateField}
                inModal
                menuInline
              />
            ) : null}
          </>
        )}
        {type === 'automation_variable' && (
          <StyledSelect
            value={state.automationVariable}
            label={t('Agentic Workflow Variable')}
            placeholder={t('Choose Merge Field')}
            options={automationVariableMergeFields}
            onChange={(option: NonObjectMergeFieldOption) => {
              setState((previous) => ({
                ...previous,
                automationVariable: option,
              }));
            }}
            inModal
            menuInline
          />
        )}
        {type === 'business' && (
          <StyledSelect
            value={state.business}
            label={t('Business Merge Field')}
            placeholder={t('Choose Merge Field')}
            options={businessMergeFields}
            onChange={(option: NonObjectMergeFieldOption) => {
              setState((previous) => ({ ...previous, business: option }));
            }}
            inModal
            menuInline
          />
        )}
        {type === 'team_member' && (
          <StyledSelect
            value={state.teamMember}
            label={t('Team Member Merge Field')}
            placeholder={t('Choose Merge Field')}
            options={teamMemberMergeFields}
            onChange={(option: NonObjectMergeFieldOption) => {
              setState((previous) => ({ ...previous, teamMember: option }));
            }}
            inModal
            menuInline
          />
        )}
      </FormContainer>
    </BasicModal>
  );
};
